import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVendorConfig } from '../context/VendorConfigContext';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  Kitchen as KitchenIcon,
  Print as PrintIcon,
  TableBar as TableIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Tv as TvIcon,
  PointOfSale as PointOfSaleIcon,
  Loyalty as LoyaltyIcon,
  CalendarMonth as CalendarMonthIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import api from '../api/api';
import { useTheme } from '../context/ThemeContext';
import { PageHeader, CardContainer, LoadingSkeleton } from '../components/ui';
import '../styles/comprehensive-responsive-system.css';
import { todayLocalIso, toLocalIsoDate } from '../utils/dateLocal';

interface SettingItem {
  title: string;
  description: string;
  icon: JSX.Element;
  path: string;
  color: string;
  feature?: 'tableManagement' | 'floorManagement' | 'sectionManagement' | 'multipleMenus' | 'kitchenDisplay' | 'orderLines' | 'stewardFeatures';
}

interface QuickStats {
  activeOrders: number;
  todaysOrders: number;
  menuItems: number;
  activeKitchens: number;
}

const VendorSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { features } = useVendorConfig();
  const [stats, setStats] = useState<QuickStats>({
    activeOrders: 0,
    todaysOrders: 0,
    menuItems: 0,
    activeKitchens: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        // Fetch vendor orders to calculate active and today's orders
        const ordersResponse = await api.get(`/vendors/${user.id}/orders`);

        // Handle paginated response or array
        let allOrders = [];
        if (ordersResponse.data && ordersResponse.data.content && Array.isArray(ordersResponse.data.content)) {
          // New paginated response format
          allOrders = ordersResponse.data.content;
        } else if (Array.isArray(ordersResponse.data)) {
          // Legacy array response
          allOrders = ordersResponse.data;
        }

        // Count active orders (PENDING, ACCEPTED, PREPARING, READY)
        const activeStatuses = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY'];
        const activeOrders = allOrders.filter((order: any) =>
          activeStatuses.includes(order.status)
        ).length;

        // Count today's orders
        const today = todayLocalIso();
        const todaysOrders = allOrders.filter((order: any) => {
          const orderDate = toLocalIsoDate(new Date(order.createdAt));
          return orderDate === today;
        }).length;
        
        // Fetch menu items count from vendor menu
        const menuResponse = await api.get(`/vendors/${user.id}/menu`);
        const categories = menuResponse.data || [];
        const menuItems = categories.reduce((total: number, category: any) => {
          return total + (category.items?.length || 0);
        }, 0);
        
        // Kitchen endpoint not implemented yet, using default value
        const activeKitchens = 0;
        
        setStats({
          activeOrders,
          todaysOrders,
          menuItems,
          activeKitchens
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.id]);

  const menuSettings: SettingItem[] = [
    {
      title: 'Menu Management',
      description: 'Manage categories, items, and prices',
      icon: <RestaurantIcon />,
      path: `/admin/vendors/${user?.id}/menu`,
      color: 'orange'
      // No feature flag - always available
    },
    {
      title: 'Multi-Menu Setup',
      description: 'Create different menus for different sections',
      icon: <MenuBookIcon />,
      path: '/vendor/multi-menu',
      color: 'purple',
      feature: 'multipleMenus'
    }
  ];

  const kitchenSettings: SettingItem[] = [
    {
      title: 'Inventory & Stock',
      description: 'Track ingredients, set recipes, and monitor stock levels',
      icon: <InventoryIcon />,
      path: '/vendor/inventory',
      color: 'amber'
    },
    {
      title: 'Kitchen Management',
      description: 'Configure kitchen stations and map menu items to kitchens',
      icon: <KitchenIcon />,
      path: '/vendor/kitchens',
      color: 'green',
      feature: 'kitchenDisplay'
    },
    {
      title: 'Kitchen Display System',
      description: 'Real-time order management for kitchen staff',
      icon: <KitchenIcon />,
      path: '/vendor/kitchen',
      color: 'purple',
      feature: 'kitchenDisplay'
    },
    {
      title: 'Printer Setup',
      description: 'Configure receipt and KOT printers for each kitchen',
      icon: <PrintIcon />,
      path: '/vendor/printers',
      color: 'blue',
      feature: 'kitchenDisplay'
    },
    {
      title: 'Order Status Display',
      description: 'Customer-facing display for order progress (TV display)',
      icon: <TvIcon />,
      path: '/vendor/order-status-display',
      color: 'cyan'
      // No feature flag - always available
    }
  ];

  const venueSettings: SettingItem[] = [
    {
      title: 'Table Layout',
      description: 'Manage floors, sections, and tables',
      icon: <TableIcon />,
      path: '/vendor/tables',
      color: 'cyan',
      feature: 'tableManagement'
    },
  ];

  const staffSettings: SettingItem[] = [
    {
      title: 'Steward Management',
      description: 'Manage stewards and table assignments',
      icon: <GroupIcon />,
      color: 'indigo',
      path: '/admin/stewards',
      feature: 'stewardFeatures'
    },
    {
      title: 'Steward Settings',
      description: 'Configure steward visibility and table assignments',
      icon: <GroupIcon />,
      color: 'indigo',
      path: '/vendor/steward-settings',
      feature: 'stewardFeatures'
    },
    {
      title: 'Steward Attendance',
      description: 'View attendance, set venue location, and approve check-ins',
      icon: <EventAvailableIcon />,
      color: 'green',
      path: '/vendor/steward-reports',
      feature: 'stewardFeatures'
    }
  ];

  const businessSettings: SettingItem[] = [
    {
      title: 'Business Configuration',
      description: 'Configure business type and enable/disable features',
      icon: <SettingsIcon />,
      color: 'gray',
      path: '/vendor/configuration'
    },
    {
      title: 'Tax Configuration',
      description: 'Configure GST, service charges, and other tax settings',
      icon: <ReceiptIcon />,
      color: 'blue',
      path: '/vendor/tax-config'
    },
    {
      // One entry, not two. Happy hours, coupons, stamp cards, points, tiers and
      // gift cards are all "offers I run" to an operator, and they now share one
      // page and one table.
      title: 'Loyalty Programs',
      description: 'Happy hours, coupons, stamp cards, points and gift cards',
      icon: <LoyaltyIcon />,
      color: 'pink',
      path: '/vendor/loyalty'
    },
    {
      title: 'Reservations',
      description: 'Manage table reservations, waitlist, and table merging',
      icon: <CalendarMonthIcon />,
      color: 'rose',
      path: '/vendor/reservations'
    },
    {
      title: 'Reports & Analytics',
      description: 'View sales reports, menu performance, and financial analytics',
      icon: <AssessmentIcon />,
      color: 'emerald',
      path: '/vendor/reports'
    }
  ];

  // Filter settings based on vendor feature configuration
  const filterByFeature = (settings: SettingItem[]) => {
    return settings.filter(item =>
      !item.feature || (features && features[item.feature])
    );
  };

  const renderSettingCard = (setting: SettingItem) => {
    const colorClasses = {
      orange: 'bg-gradient-to-br from-orange-400 to-orange-600',
      purple: 'bg-gradient-to-br from-purple-400 to-purple-600',
      green: 'bg-gradient-to-br from-green-400 to-green-600',
      emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
      amber: 'bg-gradient-to-br from-amber-400 to-orange-600',
      blue: 'bg-gradient-to-br from-blue-400 to-blue-600',
      yellow: 'bg-gradient-to-br from-yellow-400 to-amber-600',
      cyan: 'bg-gradient-to-br from-cyan-400 to-cyan-600',
      teal: 'bg-gradient-to-br from-teal-400 to-teal-600',
      pink: 'bg-gradient-to-br from-pink-400 to-pink-600',
      indigo: 'bg-gradient-to-br from-indigo-400 to-indigo-600',
      gray: 'bg-gradient-to-br from-gray-400 to-gray-600',
      red: 'bg-gradient-to-br from-red-400 to-red-600',
      slate: 'bg-gradient-to-br from-slate-400 to-slate-600',
      rose: 'bg-gradient-to-br from-rose-400 to-rose-600'
    };

    return (
      <CardContainer
        key={setting.title}
        disableHover
        data-testid={`setting-card-${setting.title.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setting.path && navigate(setting.path)}
        sx={{
          cursor: 'pointer',
          height: '100%',
          transition: 'all 0.3s ease',
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            transform: 'translateY(-4px)',
          },
        }}
      >
        <div className="p-responsive-md h-full flex flex-col">
          <div className="flex items-start gap-responsive-md flex-1">
            <div className={`icon-container-sm rounded-xl ${colorClasses[setting.color as keyof typeof colorClasses]} flex items-center justify-center text-white shadow-lg`}>
              {setting.icon}
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <h3 className="text-lg font-semibold mb-2 leading-tight" style={{ color: theme.text }}>
                {setting.title}
              </h3>
              <p className="text-sm leading-relaxed flex-1" style={{ color: theme.gray[500] }}>
                {setting.description}
              </p>
            </div>
            <ChevronRightIcon className="icon-sm flex-shrink-0 mt-1" style={{ color: theme.gray[300] }} />
          </div>
        </div>
      </CardContainer>
    );
  };

  return (
    <div className="main-content-wrapper py-6" style={{ backgroundColor: theme.background }}>
      <PageHeader
        title="Vendor Settings"
        subtitle="Manage your restaurant settings and configurations"
        data-testid="vendor-settings-header"
      />

      <div className="space-y-8">
        <div>
          <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Business Setup</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-responsive-md auto-rows-fr">
            {businessSettings.map(renderSettingCard)}
          </div>
        </div>

        {filterByFeature(menuSettings).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Menu & Items</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-responsive-md auto-rows-fr">
              {filterByFeature(menuSettings).map(renderSettingCard)}
            </div>
          </div>
        )}

        {filterByFeature(kitchenSettings).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Kitchen & Operations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-responsive-md auto-rows-fr">
              {filterByFeature(kitchenSettings).map(renderSettingCard)}
            </div>
          </div>
        )}

        {filterByFeature(venueSettings).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Venue Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-responsive-md auto-rows-fr">
              {filterByFeature(venueSettings).map(renderSettingCard)}
            </div>
          </div>
        )}

        {filterByFeature(staffSettings).length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Staff Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-responsive-md auto-rows-fr">
              {filterByFeature(staffSettings).map(renderSettingCard)}
            </div>
          </div>
        )}

        {/* Socket.IO Diagnostics and Theme & Appearance sections removed —
            diagnostics is a developer-mode tool (re-add behind a feature flag
            if needed), and the vendor-orders theme is now the standard look
            for every vendor (no per-vendor override). */}

        <div>
          <h2 className="text-xl font-semibold mb-4" style={{ color: theme.text }}>Quick Stats</h2>
          {loading ? (
            <LoadingSkeleton variant="card" count={4} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-responsive-sm">
              {[
                { value: stats.activeOrders, label: 'Active Orders', color: theme.warning || '#F59E0B' },
                { value: stats.todaysOrders, label: "Today's Orders", color: theme.success },
                { value: stats.menuItems, label: 'Menu Items', color: theme.primary },
                { value: stats.activeKitchens, label: 'Active Kitchens', color: theme.accent },
              ].map((stat) => (
                <CardContainer
                  key={stat.label}
                  disableHover
                  data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                  sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}
                >
                  <div className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="text-sm font-medium" style={{ color: theme.gray[500] }}>{stat.label}</div>
                </CardContainer>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorSettings;